import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Grid, ListItemText, MenuItem, Select, InputLabel, FormControl, Box, Avatar, Chip } from "@material-ui/core";
import { toast } from "react-toastify";
import { Facebook, Instagram, WhatsApp, HeadsetMic, SettingsPhone, Person, CheckCircle, Group as GroupIcon } from "@material-ui/icons";
import ShowTicketOpen from "../ShowTicketOpenModal";
import ColorModeContext from "../../layout/themeContext";
import { createGradient, createGradientStyles } from "../../utils/themeColors";

const useStyles = makeStyles((theme) => ({
  online: {
    fontSize: 11,
    color: "#25d366",
    fontWeight: 600
  },
  offline: {
    fontSize: 11,
    color: "#e1306c",
    fontWeight: 600
  },
  dialogTitle: {
    // Usa a cor primária do tema (configurável no white label)
    // Se gradient estiver ativo, usa degradê entre primária e secundária
    padding: theme.spacing(2, 3),
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1.2rem'
    }
  },
  // Usa o utilitário centralizado para gradientes
  ...createGradientStyles(theme),
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa'
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.type === 'dark' ? theme.palette.text.secondary : '#555',
    fontWeight: 500,
    fontSize: '0.85rem'
  },
  sectionIcon: {
    fontSize: 18,
    // Usa a cor primária do tema
    color: theme.palette.primary.main
  },
  formControl: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : '#fff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      }
    }
  },
  contactChip: {
    backgroundColor: theme.palette.type === 'dark' ? '#1b5e20' : '#e8f5e9',
    color: theme.palette.type === 'dark' ? '#fff' : '#2e7d32',
    fontWeight: 500,
    '& .MuiChip-avatar': {
      backgroundColor: '#25d366',
      color: '#fff'
    }
  },
  selectedInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : '#fff',
    borderRadius: 8,
    border: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
    marginBottom: theme.spacing(1)
  },
  selectedInfoName: {
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#fff' : '#333'
  },
  selectedInfoNumber: {
    color: theme.palette.type === 'dark' ? '#aaa' : '#666'
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
    borderTop: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0'
  },
  cancelButton: {
    color: theme.palette.type === 'dark' ? '#bbb' : '#666'
  },
  saveButton: {
    // Usa a cor primária do tema (configurável no white label)
    color: '#fff',
    '&:hover': {
      filter: 'brightness(0.9)'
    },
    '&:disabled': {
      background: theme.palette.type === 'dark' ? '#555' : '#ccc',
      color: theme.palette.type === 'dark' ? '#888' : '#999'
    }
  },
  statusConnected: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    color: '#25d366',
    fontSize: 11,
    fontWeight: 600
  },
  statusDisconnected: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    color: '#e1306c',
    fontSize: 11,
    fontWeight: 600
  },
  // Indicador de cor da fila
  queueColor: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    flexShrink: 0
  },
  // ✅ CORREÇÃO: Estilos para melhorar apresentação dos contatos no Autocomplete
  autocompleteOption: {
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
    }
  }
}));

const filter = createFilterOptions({
  trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const theme = useTheme();
  const colorModeContext = useContext(ColorModeContext);
  
  // Verifica se deve usar degradê (das configurações white label)
  const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;
  
  const [options, setOptions] = useState([]);
  const [channelFilter, setChannelFilter] = useState(null);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newContact, setNewContact] = useState({});
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Quando o modal abre com um contato inicial, sempre definir o contato selecionado
  useEffect(() => {
    if (modalOpen && initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [modalOpen, initialContact]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`/whatsapp`, { params: { companyId, session: 0 } });
          setWhatsapps(data);
          
          // ✅ Pré-selecionar conexão para usuários
          // Prioridade: 1) whatsappId do usuário, 2) única conexão disponível
          if (whatsappId !== null && whatsappId !== undefined) {
            setSelectedWhatsapp(whatsappId);
          } else if (data && data.length === 1) {
            // Se só tem uma conexão disponível, pré-seleciona
            setSelectedWhatsapp(data[0].id);
          } else if (user.profile !== 'admin' && data && data.length > 0) {
            // Para usuários não-admin, tentar encontrar conexão CONNECTED
            const connectedWhatsapp = data.find(w => w.status === 'CONNECTED');
            if (connectedWhatsapp) {
              setSelectedWhatsapp(connectedWhatsapp.id);
            }
          }
        } catch (err) {
          console.error('Erro ao buscar conexões:', err);
        }
      };

      // ✅ Pré-selecionar fila para usuários
      // Prioridade: 1) única fila, 2) primeira fila disponível para não-admin
      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id);
      } else if (user.profile !== 'admin' && user.queues.length > 0) {
        // Para usuários não-admin com múltiplas filas, pré-seleciona a primeira
        setSelectedQueue(user.queues[0].id);
      }
      
      fetchContacts();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [selectedContact, channelFilter, whatsappId, user.queues, user.profile, companyId])

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      case "whatsapp_oficial":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      default:
        return "error";
    }
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicket = async contactId => {
    if (!contactId) return;
    // if (selectedQueue === "" && user.profile !== 'admin') {
    if (selectedQueue === "") {
      toast.error(i18n.t("newTicketModal.errors.selectQueue"));
      return;
    }

    setLoading(true);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        queueId,
        whatsappId,
        userId: user.id,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {
      setLoading(false);
      
      // Se for erro 403 (Forbidden), o backend já retornou que outro usuário atende
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        setOpenAlert(true);
        setUserTicketOpen(errorData.ticket?.user?.name || i18n.t("newTicketModal.otherUser"));
        setQueueTicketOpen(errorData.ticket?.queue?.name || i18n.t("ticketsList.noQueue"));
        return;
      }

      // Para erro 409, tentar parsear o ticket existente
      if (err.response?.status === 409) {
        const ticket = JSON.parse(err.response.data.error);

        if (ticket.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(ticket?.user?.name);
          setQueueTicketOpen(ticket?.queue?.name);
        } else {
          setOpenAlert(false);
          setUserTicketOpen("");
          setQueueTicketOpen("");
          setLoading(false);
          onClose(ticket);
        }
      } else {
        // Outros erros
        toastError(err);
      }
    }
    setLoading(false);
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = contact => {
    setSelectedContact(contact);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderOption = option => {
    if (option.number) {
      return (
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1.25} 
          width="100%" 
          style={{ padding: "8px 0" }}
        >
          {/* ✅ CORREÇÃO: Ícone diferente para contato e grupo */}
          {option.isGroup ? (
            <Avatar style={{ backgroundColor: '#25d366', width: 40, height: 40 }}>
              <GroupIcon style={{ fontSize: 22, color: '#fff' }} />
            </Avatar>
          ) : (
            <Avatar 
              src={option.profilePicUrl || option.urlPicture} 
              style={{ width: 40, height: 40 }}
            >
              {!option.profilePicUrl && !option.urlPicture && (
                <Person style={{ fontSize: 22, color: '#fff' }} />
              )}
            </Avatar>
          )}
          <Box flex={1} style={{ minWidth: 0, marginLeft: '10px' }}>
            <Typography 
              variant="body1" 
              style={{ 
                fontSize: 15, 
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {option.name}
            </Typography>
            <Typography 
              variant="caption" 
              style={{ 
                fontSize: 12, 
                color: '#666',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {option.isGroup ? (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <GroupIcon style={{ fontSize: 12, color: '#25d366' }} />
                  <span>{i18n.t("contacts.group")} • {option.number}</span>
                </Box>
              ) : (
                <>
                  {IconChannel(option.channel)}
                  <span style={{ marginLeft: 4 }}>{option.number}</span>
                </>
              )}
            </Typography>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box display="flex" alignItems="center" gap={1} style={{ padding: "8px 0" }}>
          <Person style={{ fontSize: 20, color: '#999' }} />
          <Typography variant="body2" style={{ fontSize: 14, color: '#666' }}>
            {i18n.t("newTicketModal.add")} {option.name}
          </Typography>
        </Box>
      );
    }
  };

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const renderContactAutocomplete = () => {
    if (initialContact === undefined || initialContact.id === undefined) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => {
              setChannelFilter(newValue ? newValue.channel : "whatsapp");
              handleSelectOption(e, newValue)
            }}
            // ✅ CORREÇÃO: Configurações para melhorar apresentação dos contatos (1 por linha)
            ListboxProps={{
              style: {
                maxHeight: '400px', // Altura máxima da lista
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("newTicketModal.fieldLabel")}
                variant="outlined"
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                onKeyPress={e => {
                  if (loading || !selectedContact) return;
                  else if (e.key === "Enter") {
                    handleSaveTicket(selectedContact.id);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )
    }
    return null;
  }

  return (
    <>
      <Dialog 
        open={modalOpen} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        scroll="body"
        PaperProps={{
          style: {
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            margin: '32px auto',
            maxHeight: 'calc(100vh - 64px)',
          },
        }}
      >
        <DialogTitle 
          className={`${classes.dialogTitle} ${useGradient ? classes.gradientBackground : classes.solidBackground}`} 
          id="form-dialog-title"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <HeadsetMic style={{ fontSize: 24 }} />
            <span>{i18n.t("newTicketModal.newAttendance")}</span>
          </Box>
        </DialogTitle>
        
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            
            {/* CONTATO SELECIONADO (se houver) */}
            {selectedContact && (
              <Grid xs={12} item>
                <Box className={classes.selectedInfo}>
                  {/* Avatar: Foto do contato, ícone de grupo, ou ícone de pessoa */}
                  {selectedContact.isGroup ? (
                    <Avatar style={{ backgroundColor: '#25d366', width: 44, height: 44 }}>
                      <GroupIcon style={{ fontSize: 24 }} />
                    </Avatar>
                  ) : selectedContact.profilePicUrl || selectedContact.urlPicture ? (
                    <Avatar 
                      src={selectedContact.profilePicUrl || selectedContact.urlPicture} 
                      style={{ width: 44, height: 44 }}
                    />
                  ) : (
                    <Avatar style={{ backgroundColor: '#25d366', width: 44, height: 44 }}>
                      <Person style={{ fontSize: 24 }} />
                    </Avatar>
                  )}
                  <Box flex={1}>
                    <Typography variant="subtitle2" className={classes.selectedInfoName}>
                      {selectedContact.name}
                    </Typography>
                    <Typography variant="caption" className={classes.selectedInfoNumber}>
                      {selectedContact.isGroup ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <GroupIcon style={{ fontSize: 14, color: '#25d366' }} />
                          <span>{i18n.t("contacts.group")}</span>
                        </Box>
                      ) : (
                        selectedContact.number
                      )}
                    </Typography>
                  </Box>
                  <CheckCircle style={{ color: '#25d366', fontSize: 22 }} />
                </Box>
              </Grid>
            )}
            
            {/* BUSCAR CONTATO */}
            {renderContactAutocomplete()}
            
            {/* SETOR / FILA */}
            <Grid xs={12} item>
              <Typography className={classes.sectionTitle}>
                <HeadsetMic className={classes.sectionIcon} />
                {i18n.t("newTicketModal.attendanceSector")}
              </Typography>
              <FormControl fullWidth variant="outlined" className={classes.formControl}>
                <Select
                  required
                  displayEmpty
                  value={selectedQueue}
                  onChange={(e) => setSelectedQueue(e.target.value)}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => {
                    if (selectedQueue === "") {
                      return <span style={{ color: '#999' }}>{i18n.t("newTicketModal.selectSector")}</span>;
                    }
                    const queue = user.queues.find(q => q.id === selectedQueue);
                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box 
                          style={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: queue?.color || '#667eea' 
                          }} 
                        />
                        {queue?.name}
                      </Box>
                    );
                  }}
                >
                  {user.queues?.length > 0 &&
                    user.queues.map((queue, key) => (
                      <MenuItem key={key} value={queue.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box 
                            style={{ 
                              width: 10, 
                              height: 10, 
                              borderRadius: '50%', 
                              backgroundColor: queue?.color || '#667eea' 
                            }} 
                          />
                          <ListItemText primary={queue.name} />
                        </Box>
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            
            {/* CONEXÃO WHATSAPP */}
            <Grid xs={12} item>
              <Typography className={classes.sectionTitle}>
                <SettingsPhone className={classes.sectionIcon} />
                {i18n.t("newTicketModal.sendChannel")}
              </Typography>
              <FormControl fullWidth variant="outlined" className={classes.formControl}>
                <Select
                  required
                  displayEmpty
                  value={selectedWhatsapp}
                  onChange={(e) => setSelectedWhatsapp(e.target.value)}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => {
                    if (selectedWhatsapp === "") {
                      return <span style={{ color: '#999' }}>{i18n.t("newTicketModal.selectConnection")}</span>;
                    }
                    const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp);
                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        {IconChannel(whatsapp?.channel)}
                        <span>{whatsapp?.name}</span>
                        {whatsapp?.status === 'CONNECTED' ? (
                          <span className={classes.statusConnected}>
                            <CheckCircle style={{ fontSize: 12 }} /> {i18n.t("common.status.online")}
                          </span>
                        ) : (
                          <span className={classes.statusDisconnected}>{i18n.t("common.status.offline")}</span>
                        )}
                      </Box>
                    );
                  }}
                >
                  {whatsapps?.length > 0 &&
                    whatsapps.map((whatsapp, key) => (
                      <MenuItem key={key} value={whatsapp.id}>
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                          {IconChannel(whatsapp.channel)}
                          <ListItemText 
                            primary={whatsapp.name}
                            secondary={
                              whatsapp.status === 'CONNECTED' 
                                ? <span style={{ color: '#25d366', fontSize: 11 }}>● {i18n.t("common.status.connected")}</span>
                                : <span style={{ color: '#e1306c', fontSize: 11 }}>● {i18n.t("common.status.disconnected")}</span>
                            }
                          />
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            
          </Grid>
        </DialogContent>
        
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={loading}
            className={classes.cancelButton}
          >
            Cancelar
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="button"
            disabled={!selectedContact || !selectedQueue || !selectedWhatsapp}
            onClick={() => handleSaveTicket(selectedContact.id)}
            loading={loading}
            className={`${classes.saveButton} ${useGradient ? classes.gradientButton : classes.solidButton}`}
          >
            Iniciar Atendimento
          </ButtonWithSpinner>
        </DialogActions>
        
        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            initialValues={newContact}
            onClose={handleCloseContactModal}
            onSave={handleAddNewContactTicket}
          />
        )}
        
        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={handleCloseAlert}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog>
    </>
  );
};
export default NewTicketModal;
